/* istanbul ignore file - need to fix flakey tests */
import { useMemo } from 'react';
import { FlatNode } from './useFlatTree';

export type TreeNode<T> = {
  children: TreeNode<T>[];
} & T;

/**
 * A React hook that converts a flat tree structure to a nested tree structure
 *
 * @param flatData - Array of flat tree nodes with id, parent, and name properties
 * @returns Nested tree structure with children arrays
 */
export function useTree<T>(flatData: FlatNode<T>[]): TreeNode<T>[] {
  return useMemo(() => {
    if (!flatData || !Array.isArray(flatData) || flatData.length === 0) {
      return [];
    }

    // Create a map for faster lookups
    const nodeMap: Record<string, TreeNode<T>> = {};

    // Initialize all nodes with an empty children array
    flatData.forEach((item) => {
      nodeMap[item.PID] = {
        ...item,
        children: [],
      };
    });

    // Create the tree structure
    const tree: TreeNode<T>[] = [];

    // Populate children arrays
    flatData.forEach((item) => {
      const node = nodeMap[item.PID];

      // If the node has a parent, add it to the parent's children
      if (item.parent && nodeMap[item.parent]) {
        nodeMap[item.parent].children.push(node);
      }
      // If no parent (root node), add it to the tree array
      else {
        tree.push(node);
      }
    });

    return tree;
  }, [flatData]);
}
